const checkHttpError = (res) => {
  if (!res.ok) {
    return res
      .json()
      .then((error) => {
        const err = Object.assign({ code: res.status }, error || {});
        throw err;
      })
      .catch(() => {
        const err = { code: res.status };
        throw err;
      });
  }

  return res;
};

export default class Api {
  constructor(endpoint) {
    this.endpoint = endpoint;
    this.token = null;
  }

  _get(url, params, options = {}) {
    const encodedParams = [];
    if (params) {
      for (let p in params) {
        if (params.hasOwnProperty(p) && params[p] !== undefined && params[p] !== null) {
          encodedParams.push(encodeURIComponent(p) + "=" + encodeURIComponent(params[p]));
        }
      }
    }

    const headers = Object.assign(
      {},
      options.headers || {},
      this.token ? { Authorization: "Bearer " + this.token } : {}
    );
    return fetch(
      this.endpoint + url + (encodedParams.length ? "?" + encodedParams.join("&") : ""),
      {
        method: "GET",
        headers,
      }
    )
      .then(checkHttpError)
      .then((res) => (options.type === "text" ? res.text() : res.json()));
  }

  _post(url, body, options = {}) {
    const encodedParams = [];
    if (options.params) {
      for (let p in options.params) {
        if (
          options.params.hasOwnProperty(p) &&
          options.params[p] !== undefined &&
          options.params[p] !== null
        ) {
          encodedParams.push(encodeURIComponent(p) + "=" + encodeURIComponent(options.params[p]));
        }
      }
    }

    return fetch(
      this.endpoint + url + (encodedParams.length ? "?" + encodedParams.join("&") : ""),
      {
        method: "POST",
        headers: Object.assign(
          {},
          options.headers || {},
          this.token ? { Authorization: "Bearer " + this.token } : {}
        ),
        body: options.binary ? body : JSON.stringify(body),
      }
    )
      .then(checkHttpError)
      .then((res) => (options.type === "text" ? res.text() : res.json()));
  }

  login(login, password) {
    return this._post("/session/login", { login, password });
  }

  totpCode(login) {
    return this._post("/session/totpCode", { login });
  }

  totp(login, code) {
    return this._post("/session/totp", { login, code });
  }

  resetPassword(login) {
    return this._post("/session/resetPassword", { login });
  }

  validateResetCode(login, code) {
    return this._post("/session/validateCode", { login, code });
  }

  changePassword(login, password, oldPassword, code) {
    return this._post("/session/changePassword", { login, password, oldPassword, code });
  }

  logout() {
    return this._get("/session/logout");
  }

  getProfile() {
    return this._get("/user/profile");
  }

  updateProfile(profile) {
    return this._post("/user/profile", profile);
  }

  getSystemData() {
    return this._get("/user/systemData");
  }

  uploadFile(
    file,
    category = "temp",
    mimeType = "application/octet-stream",
    fileName = null,
    onProgress = null
  ) {
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded, event.total);
          }
        });
        xhr.addEventListener("loadend", () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            resolve(xhr.responseText && JSON.parse(xhr.responseText));
          } else {
            reject(xhr.responseText && JSON.parse(xhr.responseText));
          }
        });
        xhr.open("POST", this.endpoint + "/file/upload", true);
        xhr.setRequestHeader("Content-Type", mimeType);
        if (category) {
          xhr.setRequestHeader("X-Category", category);
        }
        if (fileName) {
          xhr.setRequestHeader("X-Filename", encodeURI(fileName));
        }
        if (this.token) {
          xhr.setRequestHeader("Authorization", "Bearer " + this.token);
        }
        xhr.send(file);
      });
    }
    return this._post("/file/upload", file, {
      binary: true,
      headers: Object.assign(
        {
          "Content-Type": mimeType,
        },
        category ? { "X-Category": category } : {},
        fileName ? { "X-Filename": fileName } : {}
      ),
    });
  }

  getSchools(search) {
    return this._get("/classes/schools", { search });
  }

  getClasses(school, search) {
    return this._get("/classes/classes/" + encodeURIComponent(school), { search });
  }

  setUserClass(class_id) {
    return this._post("/classes/setClass", { class_id });
  }

  getHomeworkTodo(from) {
    return this._get("/homework/todo", { from });
  }

  getHomework(id, from) {
    return this._get("/homework/get", { id, from });
  }

  saveHomework(homework) {
    return this._post("/homework/save", homework);
  }

  getMilestone(id, from) {
    return this._get("/homework/getMilestone", { id, from });
  }

  getMilestoneProofs(id, from) {
    return this._get("/homework/getMilestoneProofs", { id, from });
  }

  submitManualValidation(id, proofs) {
    return this._post("/homework/submitManualValidation", { id, proofs });
  }

  validateMilestone(id, from) {
    return this._post("/homework/validateMilestone", { id, from });
  }

  rejectMilestone(id, from) {
    return this._post("/homework/rejectMilestone", { id, from });
  }

  sendTestMail(id, params) {
    return this._post("/admin/sendTestMail", { id, params });
  }

  getNotificationsCount() {
    return this._get("/notifications/count");
  }

  getLatestNotifications() {
    return this._get("/notifications/latest");
  }

  markAsViewed(id) {
    return this._post("/notifications/markAsViewed", { id });
  }

  getNotificationsUnread() {
    return this._get("/notifications/unread");
  }

  getNotificationsRead() {
    return this._get("/notifications/read");
  }

  removeNotifications(ids) {
    return this._post("/notifications/removeNotifications", { ids });
  }

  getChildren() {
    return this._get("/user/children");
  }

  getChild(id) {
    return this._get("/user/child", { id });
  }

  getList(id) {
    return this._get("/homework/getList", { id });
  }

  saveList(list) {
    return this._post("/homework/saveList", list);
  }

  getHomeworkTypes() {
    return this._get("/homework/types");
  }

  getHomeworkCategories() {
    return this._get("/homework/categories");
  }

  startActivity(activityType, params) {
    return this._post("/activity/" + activityType + "/start", params);
  }

  validateQuestion(activityType, params) {
    return this._post("/activity/" + activityType + "/validateQuestion", params);
  }

  endActivity(activityType, params) {
    return this._post("/activity/" + activityType + "/end", params);
  }

  reviewActivity(activityType, params) {
    return this._post("/activity/" + activityType + "/review", params);
  }

  suggestVerbs(search, count) {
    return this._post("/activity/conjugation/suggestVerbs", { search, count });
  }

  suggestWords(search, count) {
    return this._post("/activity/dictation/suggestWords", { search, count });
  }
}
